/**
 * This file contains the main route for the LmC App file, and all of the
 * routes for customer-facing screens. Routes for the LmC admin dashboard and
 * legacy List/Item screens are contained in /client/App and passed here
 * as an `adminRoutes` object.
 */
import { useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { unstable_HistoryRouter as Router } from 'react-router-dom'
import './legacy-globals.css' // TODO: remove LEGACY
import './styles/less/index.less'
import './styles/octiconStyles.css' // TODO: temporary fix until I remove all the Glyph icons
import type { User, UserRole, WebSocket } from '@lmc/ts-common'
import { antdTheme, UiKitProvider } from '@lmc/ui-kit'
import { theme } from '@lmc/ui-kit/src/config/theme'
import * as Sentry from '@sentry/react'
import { App as AntApp, ConfigProvider, type ThemeConfig } from 'antd'
import { get, getJSON } from 'common/services/api'
import { getRoutes } from 'components/routes'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { QueryClient, QueryClientProvider } from 'react-query'
import { createGlobalStyle, ThemeProvider } from 'styled-components'
import initialiseSentry from './common/services/sentry'
import { Spinner } from './components/generic'
import store, { history } from './store'

const SentryRoutes = initialiseSentry()

type Role =
  | UserRole.careHomeAdmin
  | UserRole.seniorCarer
  | UserRole.groupAdmin
  | UserRole.groupManager

interface AppState {
  role: Role | null
  isLoading: boolean
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000
    }
  }
})

const GlobalStyle = createGlobalStyle`
  a:hover {
    text-decoration: none;
  }

  .ant-spotlight-arrow {
    display: none !important;
  }

  body {
    font-family: ${(props) => props.theme.font.family.sansSerif};
  }

  // remove this after we get rid of react-select
  .lmc-select__control:hover {
    border-color: ${(props) => props.theme.color.status.primary} !important;
  }
  .lmc-select__control--is-focused {
    border-color: ${(props) => props.theme.color.status.primary} !important;
  }
`

const RoutesWrapper = ({
  status,
  initResponse
}: {
  status: AppState
  initResponse: any
}) => {
  const flags = useFeatureFlags({
    flagNames: [
      'care_plans_list_screen_v2',
      'communication_records',
      'procedures'
    ] as const
  })?.data
  const carePlansListScreenV2Enabled = flags?.care_plans_list_screen_v2?.enabled

  return (
    <SentryRoutes>
      {getRoutes(status.role, initResponse, carePlansListScreenV2Enabled)}
    </SentryRoutes>
  )
}

function App() {
  const [status, setStatus] = useState<AppState>({
    isLoading: true,
    role: null
  })
  const [initResponse, setInitResponse] = useState<any>(null)

  useEffect(() => {
    get({ url: '/init' })
      .then((response) => {
        setInitResponse(response.data)

        const data = response.data as {
          user: User
          forceMfa: boolean
          webSocket: { channels: Record<WebSocket.Channels, string> }
        }

        const { user } = data

        if (!user) {
          throw new Error("Backend didn't sent back a user")
        }

        Sentry.setUser({
          email: user.email,
          id: user.id
        })

        setStatus({
          role: data.user.role as Role,
          isLoading: false
        })
        queryClient.prefetchQuery(['featureFlags'], () =>
          getJSON({ url: '/services/flagsmith' })
        )
      })
      .catch((e) => {
        console.log(e)
      })
  }, [])

  const { t } = useTranslation()

  if (status.isLoading)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <img
          style={{
            width: '20vw',
            marginTop: '40vh'
          }}
          src="/images/lmc_logo_large.png"
          alt=""
        />
        <Spinner />
      </div>
    )

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <UiKitProvider translation={t}>
          <QueryClientProvider client={queryClient}>
            <ConfigProvider theme={antdTheme as ThemeConfig}>
              <AntApp>
                {/* don't know how to fix this error
                  @ts-ignore */}
                <Router history={history}>
                  <RoutesWrapper status={status} initResponse={initResponse} />
                </Router>
              </AntApp>
            </ConfigProvider>
          </QueryClientProvider>
        </UiKitProvider>
      </ThemeProvider>
    </Provider>
  )
}

export default App
